import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  dashboardService,
  DashboardStats,
  ActivityItem,
  DeadlineItem,
  ProjectAnalyticsItem,
  TeamMemberItem,
  ProjectCardItem
} from '../services/dashboard.service';

interface DashboardState {
  // State
  stats: DashboardStats | null;
  activities: ActivityItem[];
  deadlines: DeadlineItem[];
  projectAnalytics: ProjectAnalyticsItem[];
  teamMembers: TeamMemberItem[];
  featuredProject: ProjectCardItem | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: Date | null;

  // Actions
  fetchOverview: () => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchActivities: (limit?: number) => Promise<void>;
  fetchDeadlines: (limit?: number) => Promise<void>;
  refreshData: () => Promise<void>;
  clearError: () => void;
}

export const useDashboardStore = create<DashboardState>()(
  devtools(
    (set, get) => ({
      // Initial State
      stats: null,
      activities: [],
      deadlines: [],
      projectAnalytics: [],
      teamMembers: [],
      featuredProject: null,
      isLoading: false,
      error: null,
      lastFetched: null,

      // Fetch complete overview
      fetchOverview: async () => {
        try {
          set({ isLoading: true, error: null });
          const overview = await dashboardService.getOverview();
          set({
            stats: overview.stats,
            activities: overview.activities,
            deadlines: overview.deadlines,
            projectAnalytics: overview.projectAnalytics,
            teamMembers: overview.teamMembers,
            featuredProject: overview.featuredProject || null,
            isLoading: false,
            lastFetched: new Date(),
          });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Failed to fetch dashboard overview',
          });
        }
      },

      // Fetch stats only
      fetchStats: async () => {
        try {
          set({ isLoading: true, error: null });
          const stats = await dashboardService.getStats();
          set({
            stats,
            isLoading: false,
            lastFetched: new Date(),
          });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Failed to fetch dashboard stats',
          });
        }
      },

      // Fetch activities only
      fetchActivities: async (limit?: number) => {
        try {
          set({ isLoading: true, error: null });
          const activities = await dashboardService.getActivities(limit);
          set({
            activities,
            isLoading: false,
          });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Failed to fetch activities',
          });
        }
      },

      // Fetch deadlines only
      fetchDeadlines: async (limit?: number) => {
        try {
          set({ isLoading: true, error: null });
          const deadlines = await dashboardService.getDeadlines(limit);
          set({
            deadlines,
            isLoading: false,
          });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Failed to fetch deadlines',
          });
        }
      },

      // Refresh all data
      refreshData: async () => {
        await get().fetchOverview();
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    { name: 'DashboardStore' }
  )
);

